import { createContext, useCallback, useContext, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** Accent personality themes (audit P0). `default` = brand berry. */
export type Accent = 'default' | 'blossom' | 'ocean';
export const ACCENTS: readonly Accent[] = ['default', 'blossom', 'ocean'];

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  resolved: ResolvedTheme;
  accent: Accent;
  setAccent: (a: Accent) => void;
}

const STORAGE_KEY = 's4k-theme';
const ACCENT_KEY = 's4k-accent';

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'system',
  setTheme: () => {},
  resolved: 'light',
  accent: 'default',
  setAccent: () => {},
});

function getSystemPreference(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'system') return getSystemPreference();
  return theme;
}

function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  // Cookie for SSR flash prevention (mirrors localStorage).
  document.cookie = `${STORAGE_KEY}=${resolved};path=/;max-age=31536000;SameSite=Lax`;
}

function applyAccent(accent: Accent) {
  const root = document.documentElement;
  if (accent === 'default') delete root.dataset.accent;
  else root.dataset.accent = accent;
}

/**
 * `defaultAccent` is the accent a visitor with no stored choice gets (an app can brand itself: the adult edition defaults to `ocean`). A
 * stored choice always wins. Omitted, it is `default`, the app's own colour, exactly as before.
 */
export function ThemeProvider({ children, defaultAccent = 'default' }: { children: React.ReactNode; defaultAccent?: Accent }) {
  const [theme, setThemeState] = useState<Theme>('system');
  const [resolved, setResolved] = useState<ResolvedTheme>('light');
  const [accent, setAccentState] = useState<Accent>(defaultAccent);

  // Hydrate from localStorage on mount and re-apply the current values.
  // The pre-React inline script in index.html has already added the .dark
  // class (and data-accent) for the SSR-flash case, so this keeps us in sync.
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
    const initial: Theme = stored ?? 'system';
    setThemeState(initial);
    const r = resolveTheme(initial);
    setResolved(r);
    applyTheme(r);

    const storedAccent = localStorage.getItem(ACCENT_KEY) as Accent | null;
    const initialAccent = storedAccent && ACCENTS.includes(storedAccent) ? storedAccent : defaultAccent;
    setAccentState(initialAccent);
    applyAccent(initialAccent);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrates once on mount; `defaultAccent` is a build-time constant
  }, []);

  // Live-update on system preference changes while in "system" mode.
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const r = getSystemPreference();
      setResolved(r);
      applyTheme(r);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      // localStorage may be unavailable in some embedded contexts — cookie + .dark class still apply.
    }
    const r = resolveTheme(t);
    setResolved(r);
    applyTheme(r);
  }, []);

  const setAccent = useCallback((a: Accent) => {
    setAccentState(a);
    try {
      localStorage.setItem(ACCENT_KEY, a);
    } catch {
      // Same fallback story as the mode: attribute still applies for this session.
    }
    applyAccent(a);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolved, accent, setAccent }}>
      {children}
    </ThemeContext.Provider>
  );
}

export { ThemeContext };
